/**
 * Reusable Banner Component
 * Displays different types of banners with consistent styling
 */

import {Close} from '@mui/icons-material'
import {Box, Typography, IconButton} from '@mui/material'
import React from 'react'

/**
 * Banner variant types with predefined color schemes
 */
export type BannerVariant = 'warning' | 'info' | 'success' | 'error'

/**
 * Banner style configurations for different variants
 * Colors matched to Figma design specifications
 */
const bannerStyles: Record<
  BannerVariant,
  {
    backgroundColor: string
    borderColor: string
    textColor: string
  }
> = {
  warning: {
    backgroundColor: '#fef9f2', // Figma: Semantic/Warning/warning-faded-10
    borderColor: '#ee8b00', // Figma: Semantic/Warning/warning
    textColor: '#29333d' // Figma: Foreground/foreground
  },
  info: {
    backgroundColor: '#F0F9FF', // Light blue
    borderColor: '#0EA5E9', // Blue
    textColor: '#0369A1' // Dark blue
  },
  success: {
    backgroundColor: '#F0FDF4', // Light green
    borderColor: '#22C55E', // Green
    textColor: '#166534' // Dark green
  },
  error: {
    backgroundColor: '#FEF2F2', // Light red
    borderColor: '#EF4444', // Red
    textColor: '#991B1B' // Dark red
  }
}

/**
 * Props for the Banner component
 */
interface BannerProps {
  /** The message to display in the banner */
  message: string
  /** The visual variant/type of the banner */
  variant?: BannerVariant
  /** Whether to show a border (default: true) */
  showBorder?: boolean
  /** Whether to use left border only (default: true, matches Figma design) */
  leftBorderOnly?: boolean
  /** Custom margin top (default: '16px') */
  marginTop?: string
  /** Custom margin bottom (default: '0px') */
  marginBottom?: string
  /** Custom padding (default: '16px') - Figma uses 16px spacing */
  padding?: string
  /** Custom border radius (default: '0px') - Figma design has no border radius */
  borderRadius?: string
  /** Font weight for the text (default: 400) - Figma uses Regular/400 */
  fontWeight?: number
  /** Font size (default: '16px') - Figma uses 16px */
  fontSize?: string
  /** Line height (default: '24px') - Figma uses 24px line height */
  lineHeight?: string
  /** Test ID for testing purposes */
  testId?: string
  /** Optional callback for when the banner is dismissed */
  onClose?: () => void
}

/**
 * Reusable Banner component for displaying various types of messages
 * Designed to match exact Figma specifications
 */
export const Banner: React.FC<BannerProps> = ({
  message,
  variant = 'warning',
  showBorder = true,
  leftBorderOnly = true,
  marginTop = '16px',
  marginBottom = '0px',
  padding = '16px',
  borderRadius = '0x',
  fontWeight = 400,
  fontSize = '16px',
  lineHeight = '24px',
  testId,
  onClose
}) => {
  const style = bannerStyles[variant]

  return (
    <Box
      sx={{
        marginTop,
        marginBottom,
        padding,
        backgroundColor: style.backgroundColor,
        ...(showBorder
          ? leftBorderOnly
            ? {borderLeft: `4px solid ${style.borderColor}`} // Figma: 4px left border
            : {border: `1px solid ${style.borderColor}`}
          : {}),
        borderRadius,
        display: 'flex',
        alignItems: 'flex-start', // Align to top for multi-line text
        justifyContent: 'space-between', // Space between text and close button
        gap: '8px' // Small gap between text and close button
      }}
      data-test-id={testId}
    >
      <Typography
        sx={{
          fontFamily: 'Inter', // Figma: Inter font family
          fontWeight, // Figma: Regular/400 weight
          fontSize, // Figma: 16px font size
          lineHeight, // Figma: 24px line height
          color: style.textColor, // Figma: #29333d for warning variant
          margin: 0,
          padding: 0,
          flex: 1 // Take up remaining space
        }}
      >
        {message}
      </Typography>

      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: style.textColor,
            padding: '2px',
            marginTop: '-2px'
          }}
          data-test-id={`${testId}-close-button`}
        >
          <Close fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  )
}
